import { Stack, Typography, Button } from "@mui/material";
import AnalystResponseCard from "./AnalystResponseCard";
import { Article, Recommend, CheckCircle, PlayArrow } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "motion/react";
import { extractStringValue, extractArrayItems, parsePartialJSON } from "./utils/jsonParsingUtils";
import { useResponseFallback } from "./utils/responseUtils";
import { useAiContext } from "../providers/AiProvider";
import { useCallback } from "react";

interface FinalSummaryResponseProps {
  response: string;
}

interface FinalSummaryResponseData {
  analysis_summary?: {
    key_callout?: string;
    summary_text?: string;
  };
  recommendations?: string[];
  next_steps?: string[];
}

// Helper function to extract partial final summary response fields
const extractFinalSummaryPartialFields = (jsonString: string): Partial<FinalSummaryResponseData> | null => {
  const partial: Partial<FinalSummaryResponseData> = {};

  // Extract fields from analysis_summary
  const analysisSummaryMatch = jsonString.match(/"analysis_summary"\s*:\s*\{/);
  if (analysisSummaryMatch) {
    partial.analysis_summary = {};

    // Extract key_callout
    const keyCallout = extractStringValue(jsonString, 'key_callout');
    if (keyCallout !== null) {
      partial.analysis_summary.key_callout = keyCallout;
    }

    // Extract summary_text
    const summaryText = extractStringValue(jsonString, 'summary_text');
    if (summaryText !== null) {
      partial.analysis_summary.summary_text = summaryText;
    }
  }

  // Extract recommendations
  const recommendations = extractArrayItems(jsonString, 'recommendations');
  if (recommendations.length > 0) {
    partial.recommendations = recommendations;
  }

  // Extract next_steps
  const nextSteps = extractArrayItems(jsonString, 'next_steps');
  if (nextSteps.length > 0) {
    partial.next_steps = nextSteps;
  }

  return Object.keys(partial).length > 0 || (partial.analysis_summary && Object.keys(partial.analysis_summary).length > 0) ? partial : null;
};

function FinalSummaryResponse({ response }: FinalSummaryResponseProps) {
  const theme = useTheme();
  const { setCurrentQuestion, createSessionAndStream, clearCurrentSession, isStreaming } = useAiContext();

  const summaryData = parsePartialJSON<FinalSummaryResponseData>(response, extractFinalSummaryPartialFields);
  const fallback = useResponseFallback({ response, parsedData: summaryData });

  const handleNextQuestion = useCallback(async (question: string) => {
    if (isStreaming) return;
    setCurrentQuestion(question);
    await createSessionAndStream(question);
  }, [setCurrentQuestion, createSessionAndStream, isStreaming]);

  const handleNewSession = useCallback(async (question: string) => {
    if (isStreaming) return;
    clearCurrentSession();
    setCurrentQuestion(question);
    await createSessionAndStream(question);
  }, [clearCurrentSession, setCurrentQuestion, createSessionAndStream, isStreaming]);

  if (fallback.render || !summaryData) {
    return fallback.render || <></>;
  }

  const hasAnalysisSummary =
    summaryData.analysis_summary?.key_callout ||
    summaryData.analysis_summary?.summary_text;

  const vibrantColor = theme.palette.primary.vibrant;
  const mainColor = theme.palette.primary.main;

  return (
    <Stack spacing={2}>
      <AnimatePresence>
        {hasAnalysisSummary && (
          <motion.div
            key="analysis-summary"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            <AnalystResponseCard
              title="Analysis Summary"
              content={
                <Stack spacing={2}>
                  {summaryData.analysis_summary?.key_callout && (
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {summaryData.analysis_summary.key_callout}
                    </Typography>
                  )}

                  {summaryData.analysis_summary?.summary_text && (
                    <Typography variant="body2">
                      {summaryData.analysis_summary.summary_text}
                    </Typography>
                  )}
                </Stack>
              }
              icon={<Article sx={{ color: vibrantColor }} />}
              borderColor={vibrantColor}
            />
          </motion.div>
        )}

        {summaryData.recommendations && summaryData.recommendations.length > 0 && (
          <motion.div
            key="recommendations"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              delay: 0.1,
            }}
          >
            <AnalystResponseCard
              title="Recommendations"
              content={
                <Stack spacing={1} sx={{ m: 0 }}>
                  {summaryData.recommendations.map((recommendation, index) => (
                    <Stack key={index} direction="row" alignItems="center" spacing={1}>
                      <CheckCircle fontSize="extraSmall" sx={{ color: mainColor, flexShrink: 0 }} />
                      <Typography variant="body2">
                        {recommendation}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              }
              icon={<Recommend sx={{ color: mainColor }} />}
              borderColor={mainColor}
            />
          </motion.div>
        )}

        {summaryData.next_steps && summaryData.next_steps.length > 0 && (
          <motion.div
            key="next-steps"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              delay: 0.2,
            }}
          >
            <AnalystResponseCard
              title="Next Steps"
              content={
                <Stack spacing={2} sx={{ m: 0 }}>
                  {summaryData.next_steps.map((step, index) => (
                    <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <CheckCircle fontSize="extraSmall" sx={{ color: vibrantColor, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                        {step}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        <Button
                          size="extraSmall"
                          variant="outlined"
                          onClick={() => handleNextQuestion(step)}
                          disabled={isStreaming}
                          sx={{ textTransform: 'none', py: 0 }}
                        >
                          Run Here
                        </Button>
                        <Button
                          size="extraSmall"
                          onClick={() => handleNewSession(step)}
                          disabled={isStreaming}
                          sx={{ textTransform: 'none', py: 0 }}
                        >
                          Run As New Chat
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              }
              icon={<PlayArrow sx={{ color: vibrantColor }} />}
              borderColor={vibrantColor}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
}

export default FinalSummaryResponse;

